import { BALANCE, type Balance } from './balance.js';
import type { Rng } from './rng.js';
import { type Zone, isZone, rowOf, zoneDistance } from './zones.js';

export type ShotQuality = 'PERFECT' | 'GOOD' | 'BAD';
export type KickOutcome = 'GOAL' | 'SAVE' | 'MISS' | 'POST';
export const OUTCOMES: readonly KickOutcome[] = ['GOAL', 'SAVE', 'MISS', 'POST'];

/** Удар: зона и значения с индикаторов силы и точности (0..1). */
export interface ShotInput {
  zone: Zone;
  power: number;
  accuracy: number;
}

/** Прыжок вратаря: зона и реакция 0..1 (считает сервер, не клиент; 0,5 — обычная). */
export interface KeeperInput {
  zone: Zone;
  reaction: number;
}

export interface ShotCalculation {
  quality: ShotQuality;
  missChance: number;
  /** доля промахов, попавших в каркас ворот */
  postShare: number;
}

export type OutcomeProbabilities = Record<KickOutcome, number>;

export interface PenaltyResult {
  outcome: KickOutcome;
  quality: ShotQuality;
}

export class EngineInputError extends Error {}

const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));

function unit(name: string, x: number): number {
  if (typeof x !== 'number' || !Number.isFinite(x) || x < 0 || x > 1) {
    throw new EngineInputError(`${name} must be a number in [0, 1]`);
  }
  return x;
}

function checkShot(shot: ShotInput): void {
  if (!isZone(shot.zone)) throw new EngineInputError('bad shot zone');
  unit('power', shot.power);
  unit('accuracy', shot.accuracy);
}

function checkKeeper(keeper: KeeperInput): void {
  if (!isZone(keeper.zone)) throw new EngineInputError('bad keeper zone');
  unit('reaction', keeper.reaction);
}

export function qualityOf(accuracy: number, balance: Balance = BALANCE): ShotQuality {
  if (accuracy >= balance.quality.perfect) return 'PERFECT';
  if (accuracy >= balance.quality.good) return 'GOOD';
  return 'BAD';
}

/** Качество удара и шанс промаха — не зависят от вратаря. */
export function calculateShot(shot: ShotInput, balance: Balance = BALANCE): ShotCalculation {
  checkShot(shot);
  const zb = balance.zones[shot.zone];
  const quality = qualityOf(shot.accuracy, balance);
  const op = balance.overpower;
  const overpower =
    Math.max(0, shot.power - op.threshold) * op.perUnit * (rowOf(shot.zone) === 'T' ? op.topRowFactor : 1);
  const missChance = clamp(zb.miss * balance.missByQuality[quality] + overpower, 0, balance.maxMiss);
  return { quality, missChance, postShare: zb.postShare };
}

/** Шанс сейва при условии, что мяч летит в створ. */
export function calculateSaveChance(
  shot: ShotInput,
  keeper: KeeperInput,
  balance: Balance = BALANCE,
): number {
  checkShot(shot);
  checkKeeper(keeper);
  const zb = balance.zones[shot.zone];
  const distance = zoneDistance(shot.zone, keeper.zone);
  const base = distance === 0 ? zb.saveSame : distance === 1 ? zb.saveNear : 0;
  const power = balance.savePower.base - balance.savePower.slope * shot.power;
  const reaction = balance.saveReaction.base + balance.saveReaction.slope * keeper.reaction;
  const quality = balance.saveByQuality[qualityOf(shot.accuracy, balance)];
  return clamp(base * power * reaction * quality, 0, balance.maxSave);
}

/** Точные вероятности исходов — для тестов, баланса и ботов. */
export function outcomeProbabilities(
  shot: ShotInput,
  keeper: KeeperInput,
  balance: Balance = BALANCE,
): OutcomeProbabilities {
  const { missChance, postShare } = calculateShot(shot, balance);
  const save = calculateSaveChance(shot, keeper, balance);
  const onTarget = 1 - missChance;
  return {
    MISS: missChance * (1 - postShare),
    POST: missChance * postShare,
    SAVE: onTarget * save,
    GOAL: onTarget * (1 - save),
  };
}

/** Разрешение удара. Случайность — только из rng (на сервере). */
export function resolvePenalty(
  shot: ShotInput,
  keeper: KeeperInput,
  rng: Rng,
  balance: Balance = BALANCE,
): PenaltyResult {
  const { quality, missChance, postShare } = calculateShot(shot, balance);
  const save = calculateSaveChance(shot, keeper, balance);
  if (rng() < missChance) return { quality, outcome: rng() < postShare ? 'POST' : 'MISS' };
  return { quality, outcome: rng() < save ? 'SAVE' : 'GOAL' };
}
